import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { vue } from '@/main.js'

/**
 * Extrai texto legível de um valor (string, objeto com descricao/label ou lista).
 * @param {*} valor
 * @returns {string}
 */
function formatarValor(valor) {
  if (valor === null || valor === undefined || valor === '') return '-'
  if (Array.isArray(valor)) {
    if (valor.length === 0) return '-'
    return valor
      .map((item) => formatarValor(item))
      .filter((item) => item && item !== '-')
      .join(', ')
  }
  if (typeof valor === 'object') {
    return (
      valor.descricao ||
      valor.label ||
      valor.tp_reun_ofic_treinam ||
      valor.nome ||
      valor.no_profissional ||
      '-'
    )
  }
  return String(valor)
}

/**
 * Formata a data YYYY-MM-DD ou Date para dd/mm/aaaa.
 * @param {string|Date} data
 * @returns {string}
 */
function formatarData(data) {
  if (!data) return '-'
  if (typeof data === 'string' && data.includes('/')) return data
  const str = typeof data === 'string' ? data.split('T')[0] : null
  if (str && /^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [ano, mes, dia] = str.split('-')
    return `${dia}/${mes}/${ano}`
  }
  const d = data instanceof Date ? data : new Date(data)
  if (isNaN(d.getTime())) return '-'
  const dia = String(d.getDate()).padStart(2, '0')
  const mes = String(d.getMonth() + 1).padStart(2, '0')
  const ano = d.getFullYear()
  return `${dia}/${mes}/${ano}`
}

/**
 * Formata CNS para exibição no PDF.
 * @param {string} cns
 * @returns {string}
 */
function formatarCnsPdf(cns) {
  if (!cns) return '-'
  const d = String(cns).replace(/\D/g, '')
  if (!d) return '-'
  const p1 = d.slice(0, 3)
  const p2 = d.slice(3, 7)
  const p3 = d.slice(7, 11)
  const p4 = d.slice(11, 15)
  return [p1, p2, p3, p4].filter(Boolean).join(' ')
}

/**
 * Escreve os campos no PDF com quebra de linha, respeitando a margem direita.
 * @param {jsPDF} doc
 * @param {Array<{label: string, value: string}>} campos
 * @param {number} verticalPosition
 * @returns {number}
 */
function escreverCamposComQuebra(doc, campos, verticalPosition) {
  const marginLeft = 15
  const marginRight = 15
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const maxWidth = pageWidth - marginLeft - marginRight
  const lineHeight = 6
  const bottomLimit = pageHeight - 40

  doc.setFontSize(11)
  doc.setFont(undefined, 'normal')

  campos.forEach((campo) => {
    const texto = `${campo.label}: ${campo.value}`
    const linhas = doc.splitTextToSize(texto, maxWidth)

    if (verticalPosition + linhas.length * lineHeight > bottomLimit) {
      doc.addPage()
      verticalPosition = 20
    }

    doc.text(linhas, marginLeft, verticalPosition)
    verticalPosition += linhas.length * lineHeight
  })

  // Data de emissão (padrão do bodyPdf do sistema)
  const agora = new Date()
  const dataAtual = `${String(agora.getDate()).padStart(2, '0')}/${String(
    agora.getMonth() + 1
  ).padStart(2, '0')}/${agora.getFullYear()}`

  if (verticalPosition + lineHeight > bottomLimit) {
    doc.addPage()
    verticalPosition = 20
  }

  doc.setFontSize(12)
  doc.text(`Data: ${dataAtual}`, marginLeft, verticalPosition)
  verticalPosition += lineHeight

  return verticalPosition
}

/**
 * Gera e baixa o PDF com as informações da atividade coletiva finalizada.
 * @param {Object} dados - Dados formatados da atividade
 */
export function gerarPdfAtividade(dados) {
  const titlePdf = 'Atividade Coletiva'
  const dataArquivo = formatarData(dados.data).replace(/\//g, '-')
  const titlePdfSave = utils.methods.titlePdf(
    titlePdf,
    dataArquivo !== '-' ? dataArquivo : 'finalizada'
  )
  const descriptionPdf = 'Comprovante das informações da atividade realizada'

  const doc = new jsPDF(utils.methods.encryptionPdf())
  const brasao = store.state.infoMunicipio && store.state.infoMunicipio.brasao

  const gerarConteudo = (base64Brasao) => {
    let verticalPosition = utils.methods.headerPdf(
      doc,
      base64Brasao || '',
      titlePdf,
      descriptionPdf
    )

    const campos = [
      { label: 'Data da atividade', value: formatarData(dados.data) },
      { label: 'Turno', value: dados.turno || '-' },
      {
        label: 'N° de participantes',
        value: String(dados.numeroParticipantes ?? '-'),
      },
      { label: 'Hora de início', value: dados.horaInicio || '-' },
      { label: 'Hora de fim', value: dados.horaFim || '-' },
      {
        label: 'Carga horária (horas)',
        value: String(dados.qtdHoras ?? '-'),
      },
      { label: 'Forma', value: dados.forma || '-' },
      { label: 'Tipo', value: dados.tipo || '-' },
      { label: 'Local da atividade', value: dados.local || '-' },
      { label: 'Tipo da atividade', value: dados.atividadeTipo || '-' },
      { label: 'Tema da reunião', value: dados.temasParaReuniao || '-' },
      { label: 'Temas para a saúde', value: dados.temasParaSaude || '-' },
      { label: 'Público-alvo', value: dados.publicoAlvo || '-' },
      { label: 'Práticas em saúde', value: dados.praticasEmSaude || '-' },
      { label: 'Anotações', value: dados.anotacoes || '-' },
    ].filter((item) => item.value && item.value !== '-')

    verticalPosition = escreverCamposComQuebra(doc, campos, verticalPosition)

    const profissionais = Array.isArray(dados.profissionais)
      ? dados.profissionais
      : []

    if (profissionais.length > 0) {
      const pageHeight = doc.internal.pageSize.getHeight()
      if (verticalPosition + 20 > pageHeight - 40) {
        doc.addPage()
        verticalPosition = 20
      }

      verticalPosition += 4
      doc.setFontSize(11)
      doc.setFont(undefined, 'bold')
      doc.text('Profissionais', 15, verticalPosition)
      verticalPosition += 4

      const body = profissionais.map((prof) => [
        `${prof.nome || prof.no_profissional || '-'}${
          prof.isResponsavel || prof.logado ? ' (Responsável)' : ''
        }`,
        formatarCnsPdf(
          prof.cnsProfissional || prof.nu_cns || prof.cns || ''
        ),
        prof.no_unidade_saude || '-',
        prof.nu_ine || '-',
      ])

      doc.autoTable({
        head: [['Nome', 'CNS', 'Unidade saúde', 'INE']],
        body,
        startY: verticalPosition,
        theme: 'striped',
        headStyles: {
          fillColor: '#ccc',
          fontSize: 8,
          textColor: '#000',
          halign: 'center',
        },
        bodyStyles: {
          fontSize: 8,
          overflow: 'linebreak',
          cellWidth: 'wrap',
          halign: 'center',
        },
        columnStyles: {
          0: { cellWidth: 55 },
          1: { cellWidth: 45 },
          2: { cellWidth: 55 },
          3: { cellWidth: 25 },
        },
        margin: { left: 14, right: 14 },
      })

      verticalPosition = doc.autoTable.previous.finalY
    }

    utils.methods.footerPdf(doc, verticalPosition)
    doc.save(`${titlePdfSave}.pdf`)

    vue.$toast.success('PDF gerado com sucesso!', 'Sucesso', {
      position: 'topRight',
    })
  }

  if (brasao) {
    utils.methods
      .fileToBase64(brasao)
      .then((base64Brasao) => gerarConteudo(base64Brasao))
      .catch((error) => {
        console.error(error)
        gerarConteudo('')
      })
  } else {
    gerarConteudo('')
  }
}

/**
 * Monta o objeto de dados do PDF a partir do form da ficha.
 * @param {Object} params
 * @param {Object} params.formData - fichaAtividadeColetivaTransport
 * @param {Object|null} params.tipoSelecionado - tipo do cabeçalho (oficina)
 * @param {string|null} params.cnesLabel
 * @param {string|null} params.inepLabel
 * @returns {Object}
 */
export function montarDadosPdfAtividade({
  formData,
  tipoSelecionado = null,
  cnesLabel = null,
  inepLabel = null,
}) {
  const mapaTurno = { 1: 'Manhã', 2: 'Tarde', 3: 'Noite' }
  const mapaForma = {
    presencial: 'Presencial',
    telepresenca: 'Telepresença',
    online: '100% Online',
  }

  let local = '-'
  if (formData.cnes) {
    local = cnesLabel || `CNES ${formData.cnes}`
  } else if (formData.inep) {
    local = inepLabel || `INEP ${formData.inep}`
  } else if (formData.outraLocalidade) {
    local = formData.outraLocalidade
  }

  const tipo =
    (tipoSelecionado &&
      (tipoSelecionado.tp_reun_ofic_treinam || tipoSelecionado.label)) ||
    formatarValor(formData.tipo)

  return {
    data: formData.data,
    turno: mapaTurno[formData.turno] || formData.turno || '-',
    numeroParticipantes: formData.numeroParticipantes,
    horaInicio: formData.horaInicio,
    horaFim: formData.horaFim,
    qtdHoras: formData.qtdHoras,
    forma: mapaForma[formData.forma] || formData.forma || '-',
    tipo,
    local,
    atividadeTipo: formatarValor(formData.atividadeTipo),
    temasParaReuniao: formatarValor(formData.temasParaReuniao),
    temasParaSaude: formatarValor(formData.temasParaSaude),
    publicoAlvo: formatarValor(formData.publicoAlvo),
    praticasEmSaude: formatarValor(formData.praticasEmSaude),
    anotacoes: (formData.anotacoes || '').trim() || '-',
    profissionais: formData.profissionais || [],
  }
}
